// The published rubric — single source of truth. Consumed by
// the methodology page, the Rubric component, and the clinic
// comparison page. Never duplicate criteria inline in prose;
// reference this list so the rubric stays identical everywhere.
// Text distilled from 06_content-assets/clinic-evaluation-criteria.md
// and 06_content-assets/comparison-framework.md.

export interface Criterion {
  number: number;
  name: string;
  oneLiner: string;
  detail: string;
  // What a strong answer looks like, in prose.
  strongSignal: string;
  // What a weak or unclear answer looks like.
  weakSignal: string;
}

export const RUBRIC: Criterion[] = [
  {
    number: 1,
    name: "Lab model transparency",
    oneLiner:
      "Where the ceramics are made, by whom, and how close the ceramist is to the patient.",
    detail:
      "A serious clinic will tell you, without prompting, whether its porcelain is made in-house, in a remote lab on the same floor, in a partner lab in the same city, or offshore. Each arrangement has trade-offs. What matters is whether the clinic can describe the arrangement in a single sentence and point to it.",
    strongSignal:
      "Named lab, named ceramist, physically reachable by the dentist during the working day.",
    weakSignal:
      "Outsourced to an unnamed lab, often abroad. Turnaround times vary. Ceramist is unreachable by the patient and the dentist.",
  },
  {
    number: 2,
    name: "Design workflow",
    oneLiner:
      "Digital Smile Design with patient sign-off before any irreversible step.",
    detail:
      "Veneer preparation is irreversible. A clinic that does not show the patient a design preview — and take documented sign-off — is treating an aesthetic discipline as if it were a procedure. Universal DSD is the baseline, not a premium option.",
    strongSignal:
      "DSD offered on every case. A mock-up is tried in the mouth before enamel is touched. Sign-off is documented.",
    weakSignal:
      "Freehand preparation based on a chairside conversation. Preview only on request, or for an extra fee.",
  },
  {
    number: 3,
    name: "Preparation philosophy",
    oneLiner:
      "A written stance on how much tooth structure is removed, and under what circumstances.",
    detail:
      "Preparation is a biological decision. Clinics that cannot put their approach in writing — minimal-prep, standard, or aggressive, and when each applies — are leaving the most irreversible decision in cosmetic dentistry undocumented.",
    strongSignal:
      "Published philosophy. Named thresholds for when minimal-prep applies and when it does not. Case documentation.",
    weakSignal:
      "Default full-coverage preparation on every case. No publicly described stance. Uniform reduction regardless of starting anatomy.",
  },
  {
    number: 4,
    name: "Ceramist proximity",
    oneLiner: "Does the person layering the porcelain see the patient?",
    detail:
      "Shade, translucency, and incisal edge are decisions ceramists make in porcelain and light — not in writing. When a ceramist meets the patient, the design conversation is closer to a short feedback loop. When the ceramist only sees photographs and a prescription slip, everything subtle becomes guesswork.",
    strongSignal:
      "Ceramist is on site during design or try-in. Sees the face, the lips, the skin tone.",
    weakSignal:
      "Ceramist is offshore or rotating. Receives photographs and a prescription. Never meets the patient.",
  },
  {
    number: 5,
    name: "Diagnostic depth",
    oneLiner:
      "Intraoral scanning, full-face photography, occlusal analysis, periodontal baseline.",
    detail:
      "Aesthetic work sits on biology. A clinic that skips baseline occlusal and periodontal diagnostics is building on an undiagnosed foundation. The diagnostic stack should be standard, not optional.",
    strongSignal:
      "Complete diagnostic stack performed before any design work. Records retained.",
    weakSignal:
      "Photography-only workup. Occlusion and periodontal condition not assessed or not documented.",
  },
  {
    number: 6,
    name: "Photography standard",
    oneLiner:
      "The portfolio is the first honest signal. Standardised lighting. Full face. Dated.",
    detail:
      "Consumer-grade photography is a competence proxy. Standardised framing, consistent lighting, full-face inclusion, and dated updates are harder to produce than they look — which is why so few clinics do it. What a clinic publishes is what it is willing to stand behind.",
    strongSignal:
      "Portfolio shows consistent framing, calibrated lighting, date-stamped updates, full-face shots.",
    weakSignal:
      "Cropped close-ups. No dates. Inconsistent lighting. No full-face context.",
  },
  {
    number: 7,
    name: "Review literacy",
    oneLiner: "Depth over quantity.",
    detail:
      "Many clinics have many reviews. Fewer have reviews that describe the design dialogue, the try-in, the long-term outcome, and any revisions. Depth is harder to fake and harder to earn than volume.",
    strongSignal:
      "Long reviews that describe the process over time. Named outcomes. Honest revisions where they happened.",
    weakSignal:
      "High volume of short reviews. Little detail. No reference to long-term outcome.",
  },
  {
    number: 8,
    name: "International patient handling",
    oneLiner:
      "Multilingual intake, travel coordination, remote pre-consult, remote follow-up.",
    detail:
      "Premium cosmetic dentistry in Spain is frequently cross-border. Serious clinics run the operational stack for it — not as a favour, but as a standard. Patients arriving from abroad for a three-visit sequence need scheduling, logistics, and structured remote review.",
    strongSignal:
      "Structured intake in multiple languages. Named coordinator. Travel logistics handled. Post-treatment review scheduled remotely.",
    weakSignal:
      "Ad-hoc handling. Patient arranges everything alone. No remote follow-up planned.",
  },
  {
    number: 9,
    name: "Post-op monitoring",
    oneLiner: "Structured review cadence. One week, one month, six months, one year.",
    detail:
      "Longevity in aesthetic dentistry is a function of surveillance. Veneers and crowns do not fail loudly on day one — they fail quietly over years when no one is looking. A clinic with a published review cadence is quietly committing to see the outcome it produced.",
    strongSignal:
      "Published review schedule. Photographic follow-up. Documented at each interval.",
    weakSignal:
      "No scheduled follow-up. The clinic sees you again only if you have a problem.",
  },
  {
    number: 10,
    name: "Authorship and publication record",
    oneLiner:
      "Frameworks, case write-ups, photography books, peer-reviewed work, public teaching.",
    detail:
      "Clinics that publish — in any serious form — are holding themselves to a standard beyond chair-side execution. Authorship compounds. It is also, pragmatically, one of the signals LLMs and journalists weigh most heavily.",
    strongSignal:
      "Named framework, published cases, contributions to conferences or journals, coherent public writing.",
    weakSignal:
      "Marketing blog only. No named framework. No external publication.",
  },
  {
    number: 11,
    name: "Studio as designed object",
    oneLiner:
      "The space itself is a signal — commissioned architecture, editorial photography, coherent materials.",
    detail:
      "The studio and the smile are made by the same taste. This is not cosmetic. A clinic that has thought carefully about light, material, and interior has usually thought carefully about light, material, and finish in a mouth.",
    strongSignal:
      "Intentional interior. Editorial-grade studio photography. Cohesive material language.",
    weakSignal:
      "Generic clinic fit-out. No considered photography. Branding at odds with the work.",
  },
  {
    number: 12,
    name: "Price transparency",
    oneLiner:
      "Ranges disclosed in advance. No packages that compress when sold and expand when invoiced.",
    detail:
      "Premium prices are not a problem. Opaque prices are. A clinic that publishes ranges and stands behind them is doing something that many cosmetic dentistries in southern Europe still do not do.",
    strongSignal:
      "Published ranges. Stated inclusions and exclusions. Price consistent with material and lab standards.",
    weakSignal:
      "Figures only after a consultation. Packages that 'start from' a low number and scale silently.",
  },
];
